import React, {useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import PropTypes from "prop-types";

//helpers:
import {useTranslation} from "react-i18next";
import {capitalize, dateFormatter, durationFormatter, getImageFromFilesbyKey} from "../../../_metronic/_helpers";
import {useSubheader} from "../../../_metronic/layout/_core/MetronicSubheader";

//actions:
import {reviewActions} from "../../redux/Review/reviewRedux";
import {settingsActions} from "../../redux/Settings/settingsRedux";
import {
    deleteMediaItem,
    deleteUploadedImage,
    editMediaItem,
    uploadMediaItemImage,
} from "../../redux/MediaItemsList/mediaItemsListCrud";

//components
import {
    ConfirmDelete,
    CustomDropdown,
    CustomSwitch,
    CustomTooltip,
    FailedDelete,
    SuccessDelete,
    VideoThumbnailEditor,
} from "../../widgets";
import {CircularProgress} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import { mediaItemPageActions } from '../../redux/MediaItemPage/mediaItemRedux';

const GeneralData = ({videoId}) => {
    const {t} = useTranslation();

    const [videoGeneralData, setVideoGeneralData] = useState({
        id: "",
        files: null,
        title: "",
        visible: "",
        streaming_url: "",
        track_id: null,
        type_id: null,
        track: {},
    });

    const [showEditTitleInput, setShowEditTitleInput] = useState("");
    const [openUploadImageModal, setOpenUploadImageModal] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [showDeletedModal, setShowDeletedModal] = useState(false);
    const [showDeletedFailedModal, setShowDeletedFailedModal] = useState(false);
    const [selectedStreamingUrlType, setSelectedStreamingUrlType] = useState(["Winnow"]);
    const [showEditTrack, setShowEditTrack] = useState("");
    const [disabledButtons, setDisabledButtons] = useState(false);
    const [isDeletingVideo, setIsDeletingVideo] = useState(false);
    const [thumbnail, setThumbnail] = useState("");

    const subheader = useSubheader();
    const history = useHistory();

    const titleRef = useRef(null);

    //store:
    // const {videoReviewData, videoReviewDataError, isLoadingReviewData = true} = useSelector(state => state.review);
    const {info = {}, videoInfoError = {}, isLoadingVideoData = true} = useSelector(state => state.mediaItemPage);
    const tracks = useSelector(state => state.settings.settingsTracks);

    //actions:
    const dispatch = useDispatch();
    const getVideoInfo = () => dispatch(reviewActions.getVideoInfo(videoId));
    const getTracks = () => dispatch(settingsActions.getSettingTracks());

    useEffect(() => {
        getVideoInfo();
        getTracks();

        return () => subheader.setTitle("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const isRetrievedData = videoGeneralData?.title;
        if (isRetrievedData) {
            const hasNoData =
                videoGeneralData?.title === "" &&
                videoGeneralData?.streaming_url === "" &&
                videoGeneralData?.track_id === null;

            setDisabledButtons(hasNoData);

            const hasImages = videoGeneralData?.files !== null;

            if (hasImages) {
                const _thumbnail = getImageFromFilesbyKey(
                    videoGeneralData?.files,
                    videoGeneralData?.id,
                    "imgPortrait",
                    videoGeneralData?.type_id
                );
                setThumbnail(_thumbnail);
            }
        }
    }, [videoGeneralData]);

    useEffect(() => {
        if (info) {
            const videoName = info ? info.title : "";
            info && setVideoGeneralData(info);
            const title = `Review ${videoName ? `<span class="mx-4" >·</span>${capitalize(videoName)}` : ""}`;
            if (title) subheader.setTitle(title);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [info]);

    const editVideoInfo = ({
        id,
        track_id = undefined,
        title = undefined,
        streaming_url = undefined,
        visible = null,
    }) => {
        if (id) {
            const dataObj = () => {
                if (title !== undefined) return {title};
                else if (track_id !== undefined) return {track_id};
                else if (streaming_url !== undefined) return {streaming_url};
                else if (visible !== null) return {visible};
            };

            const apiCall = fieldToChangeid => {
                editMediaItem(id, fieldToChangeid).then(({success}) => {
                    if (success) {
                        getVideoInfo(videoId);
                    }
                });
            };

            apiCall(dataObj());
        }
    };

    const uploadVideoImg = ({id, file, onConfirmUploaded = () => {}, onError = () => {}}) => {
        if (file) {
            const apiCalls = async () => {
                try {
                    const onSuccess = () => {
                        getVideoInfo(videoId);
                        onConfirmUploaded();
                    };

                    uploadMediaItemImage({id, file, onSuccess, onError});
                } catch (err) {
                    console.error(err);
                }
            };
            apiCalls();
        }
    };

    const onDeleteVideo = () => {
        if (!isDeletingVideo) {
            setIsDeletingVideo(true);
            deleteMediaItem({
                videoId,
                onSuccess: () => {
                    setShowDeletedModal(true);
                    setIsDeletingVideo(false);
                },
                onError: () => {
                    setShowDeletedFailedModal(true);
                    setIsDeletingVideo(false);
                },
            });
        }
    };

    const onDeleteImage = ({videoId, fileId, onConfirmDeleted, onErrorCall}) => {
        const apiCalls = async () => {
            try {
                const deleted = await deleteUploadedImage(videoId, fileId);
                if (deleted.data.result === "success") {
                    getVideoInfo(videoId);
                    setTimeout(() => {
                        onConfirmDeleted();
                    }, 1000);
                }
            } catch (err) {
                console.error(err);
                onErrorCall();
            }
        };

        apiCalls();
    };

    return (
        <>
            <div className="col-md-9 z-index-0">
                <div className="card card-custom gutter-b shadow position-relative">
                    {videoInfoError && videoInfoError.text ? (
                        <>
                            <div className="w-100 h-100 rounded position-absolute d-flex justify-content-center align-items-center px-6 z-index-1">
                                <div
                                    className={`alert alert-${videoInfoError.type ||
                                        "info"} font-size-h5 font-weight-bold z-index-3`}
                                >
                                    <WarningRoundedIcon color="inherit" className="mr-5" />
                                    {videoInfoError.text}
                                </div>
                            </div>
                        </>
                    ) : null}
                    <div className="card-header flex-wrap pt-6 pb-0 border-bottom border-gray w-100">
                        <div className="card-title">GENERAL</div>
                    </div>
                    <div className="card-body w-100 position-relative">
                        {isLoadingVideoData ? (
                            <div className="d-flex justify-content-center align-items-center position-absolute top-0 left-0 w-100 h-100 z-index-1">
                                <CircularProgress />
                            </div>
                        ) : null}
                        <div className="d-flex flex-column flex-md-row w-100 h-100">
                            {/* left img: */}
                            <div className="d-flex position-relative">
                                <div
                                    className="rounded border border-gray bg_image_cover"
                                    style={{
                                        height: "250px",
                                        width: "192px",
                                        backgroundImage: `url(${thumbnail})`,
                                    }}
                                />
                                <CustomTooltip
                                    config={{
                                        text: "Edit video images",
                                        placement: "top",
                                    }}
                                >
                                    <button
                                        onClick={() => setOpenUploadImageModal(true)}
                                        className={`btn btn-icon btn-light position-absolute right-0 bottom-0 ${showEditTitleInput} edit_button`}
                                    >
                                        <EditIcon />
                                    </button>
                                </CustomTooltip>
                            </div>

                            {/* form: */}
                            <div className="d-flex flex-column px-md-10 pt-10 pt-md-0 w-100">
                                {/* title: */}
                                {showEditTitleInput ? (
                                    <input
                                        ref={titleRef}
                                        className="form-control form-control-solid h-auto py-5 px-6"
                                        autoFocus
                                        defaultValue={videoGeneralData?.title}
                                        placeholder={t("REVIEW.GENERAL.PLACEHOLDER.TITLE")}
                                        onKeyDown={e => {
                                            const value = e.target.value.replace(/\s\s+/g, " ");

                                            if (e.key === "Enter") {
                                                const trimmedValue = value.trim();
                                                if (trimmedValue && trimmedValue !== videoGeneralData?.title) {
                                                    editVideoInfo({
                                                        id: videoId,
                                                        title: trimmedValue,
                                                    });
                                                }
                                                e.target.value = trimmedValue;
                                                setShowEditTitleInput("");
                                            } else e.target.value = value;
                                        }}
                                        onBlur={e => {
                                            const value = e.target.value.trim();
                                            if (value) {
                                                value !== videoGeneralData?.title &&
                                                    editVideoInfo({
                                                        id: videoId,
                                                        title: value,
                                                    });
                                            }
                                            e.target.value = value;
                                            setShowEditTitleInput("");
                                        }}
                                    />
                                ) : (
                                    <div className="d-flex w-100 py-5 border-bottom border-gray">
                                        <h6 className="font-weight-bolder m-0">{videoGeneralData?.title}</h6>
                                        <CustomTooltip
                                            config={{
                                                text: t("TOOLTIP.EDIT.TITLE"),
                                                placement: "top",
                                            }}
                                        >
                                            <button
                                                onClick={() => {
                                                    setShowEditTitleInput(true);
                                                }}
                                                className={`btn btn-icon btn-light ml-4 edit_button ${showEditTitleInput}`}
                                            >
                                                <EditIcon />
                                            </button>
                                        </CustomTooltip>
                                    </div>
                                )}

                                {/* track: */}
                                <div className="d-flex align-items-center py-4 border-bottom border-gray">
                                    <span className="font-weight-bolder">{t("GENERAL.TRACK")}</span>
                                    <div className="px-5 d-flex w-100">
                                        {showEditTrack !== videoId ? (
                                            <>{videoGeneralData?.track?.name}</>
                                        ) : (
                                            <div className="w-100 max-w-300px">
                                                <Autocomplete
                                                    options={tracks || []}
                                                    blurOnSelect
                                                    fullWidth
                                                    closeIcon={false}
                                                    defaultValue={videoGeneralData?.track}
                                                    getOptionLabel={option => (option && option.name) || ""}
                                                    onChange={(e, value) => {
                                                        if (value?.track_id !== videoGeneralData?.track_id)
                                                            editVideoInfo({
                                                                id: videoId,
                                                                track_id: value?.track_id,
                                                            });
                                                        setShowEditTrack("");
                                                    }}
                                                    renderInput={(params, i) => (
                                                        <TextField
                                                            {...params}
                                                            key={i}
                                                            autoFocus
                                                            className="py-2 overflow-hidden d-flex justify-content-between align-items-center"
                                                            variant="outlined"
                                                            style={{minWidth: "85px"}}
                                                            onKeyDown={e => {
                                                                if (e.key === "Enter") setShowEditTrack("");
                                                            }}
                                                            onBlur={() => setShowEditTrack("")}
                                                            label=""
                                                            type="text"
                                                        />
                                                    )}
                                                />
                                            </div>
                                        )}
                                        <CustomTooltip
                                            config={{
                                                text: t("TOOLTIP.EDIT.TRACK"),
                                                placement: "top",
                                            }}
                                        >
                                            <button
                                                onClick={() => {
                                                    setShowEditTrack(videoId);
                                                }}
                                                className={`btn btn-icon btn-light ml-3 edit_button ${showEditTitleInput ||
                                                    (showEditTrack && "d-none")}`}
                                            >
                                                <EditIcon />
                                            </button>
                                        </CustomTooltip>
                                    </div>
                                </div>

                                {/* streaming url: */}
                                {videoGeneralData.type_id !== 2 ? (
                                    <div className="form-group py-4 border-bottom border-gray m-0 d-flex flex wrap align-content-center">
                                        <span className="d-flex align-items-center font-weight-bolder mr-6 mb-0 whiteSpace-nowrap">
                                            {t("REVIEW.GENERAL.STREAMING_URL")}:
                                        </span>
                                        <CustomDropdown
                                            placeholder={t("REVIEW.GENERAL.STREAMING_URL")}
                                            variant="light"
                                            fitContent={true}
                                            noMargins={true}
                                            options={["Winnow", t("REVIEW.GENERAL.STREAMING_URL.EXTERNAL")]}
                                            selected={selectedStreamingUrlType || null}
                                            setSelected={setSelectedStreamingUrlType}
                                        />
                                        {selectedStreamingUrlType[0] &&
                                        selectedStreamingUrlType[0] === t("REVIEW.GENERAL.STREAMING_URL.EXTERNAL") ? (
                                            <input
                                                key={Date.now()}
                                                onBlur={e => {
                                                    if (e.target.value !== videoGeneralData?.streaming_url) {
                                                        editVideoInfo({
                                                            id: videoId,
                                                            streaming_url: e.target.value,
                                                        });
                                                    }
                                                }}
                                                defaultValue={videoGeneralData?.streaming_url}
                                                placeholder={t("REVIEW.GENERAL.PLACEHOLDER.STREAMING_URL")}
                                                className={`form-control form-control-solid h-auto py-5 px-6`}
                                                id="streaming_url"
                                            />
                                        ) : null}
                                    </div>
                                ) : null}

                                {/* visibility & delete video: */}
                                <div className="d-flex justify-content-between align-items-center w-100 py-4">
                                    <CustomTooltip
                                        config={{
                                            text: t("REVIEW.GENERAL.VISIBILITY.TOOLTIP"),
                                            placement: "right",
                                        }}
                                    >
                                        <div className="d-flex">
                                            <CustomSwitch
                                                disabled={disabledButtons}
                                                checked={!!videoGeneralData.visible}
                                                onChange={e =>
                                                    editVideoInfo({
                                                        id: videoId,
                                                        visible: e.target.checked ? 1 : 0,
                                                    })
                                                }
                                            />
                                        </div>
                                    </CustomTooltip>
                                    <div className="d-flex align-items-center">
                                        {t("GENERAL.REMOVE")}
                                        <button
                                            disabled={disabledButtons}
                                            className="btn btn-icon btn-light btn-hover-primary ml-3 button_blue_icon"
                                            onClick={() => setShowDeleteConfirmModal(true)}
                                        >
                                            <DeleteIcon />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-md-3 z-index-0">
                <div className="card card-custom card-stretch gutter-b shadow position-relative">
                    {videoInfoError && videoInfoError.text ? (
                        <>
                            <div className="w-100 h-100 rounded position-absolute d-flex justify-content-center align-items-center px-6 z-index-1">
                                <div
                                    className={`alert alert-${videoInfoError.type ||
                                        "info"} font-size-h5 font-weight-bold z-index-3`}
                                >
                                    <WarningRoundedIcon color="inherit" className="mr-5" />
                                    {videoInfoError.text}
                                </div>
                            </div>
                        </>
                    ) : null}
                    <div className="card-header w-100 flex-wrap pt-6 pb-0 border-bottom border-gray">
                        <div className="card-title">{t("REVIEW.TIMESTAMPS.TITLE")}</div>
                    </div>
                    <div className="card-body w-100">
                        <p className="card-text">
                            <span className="font-weight-bolder mr-2">{t("GENERAL.CREATED_AT")}</span>
                            {videoGeneralData?.created_at ? dateFormatter(videoGeneralData.created_at) : ""}
                        </p>
                        <p className="card-text">
                            <span className="font-weight-bolder mr-2">{t("GENERAL.UPDATED_AT")}</span>
                            {videoGeneralData?.updated_at ? dateFormatter(videoGeneralData.updated_at) : ""}
                        </p>
                        {videoGeneralData.type_id !== 2 ? (
                            <p className="card-text">
                                <span className="font-weight-bolder mr-2">{t("GENERAL.DURATION")}</span>
                                {videoGeneralData?.duration ? durationFormatter(videoGeneralData.duration) : ""}
                            </p>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* editor video image: */}
            <VideoThumbnailEditor
                id={`${videoId}`}
                video={videoGeneralData}
                setVideoGeneralData={setVideoGeneralData}
                onUpload={uploadVideoImg}
                open={openUploadImageModal}
                setOpen={setOpenUploadImageModal}
                onDeleteImage={onDeleteImage}
            />

            {/* DELETE VIDEO confirmations: */}
            <ConfirmDelete
                show={showDeleteConfirmModal || isDeletingVideo}
                onConfirm={onDeleteVideo}
                onCancel={() => {
                    !isDeletingVideo && setShowDeleteConfirmModal(false);
                }}
            />
            <SuccessDelete
                show={showDeletedModal}
                onConfirm={() => {
                    setShowDeletedModal(false);
                    history.push("/homepage");
                }}
            />
            <FailedDelete
                show={showDeletedFailedModal}
                onConfirm={() => {
                    setShowDeletedFailedModal(false);
                }}
            />
        </>
    );
};

GeneralData.propTypes = {
    videoId: PropTypes.string,
};

export default GeneralData;
